import React, { useEffect, useState, useRef } from 'react'
import { lightThemeColors } from '../../assets/styles/colors';
import styled from 'styled-components';
import { ReactComponent as Bin } from "../../assets/icons/bin-emotii.svg"

const EmailRolePair = ({
    pair,
    handleRemove,
    handleChangeEmailRolePair,
    index,
    noDelete,
    readOnlyInput,
    availableProducts = [],
    showRolePlaceholder = true
}) => {

    const [inputValue, setInputValue] = useState(pair.email)
    const [roleValue, setRoleValue] = useState(pair.role?.value || "");
    const [selectedSlugs, setSelectedSlugs] = useState(pair.product_slugs || []);
    const isFirstRender = useRef(true);

    const selectOptions = [
        { value: "admin", label: "Administrator" },
        { value: "member", label: "Custom" },
    ];

    const handleInputChange = (e) => {
        if (readOnlyInput) return;
        setInputValue(e.currentTarget.value);
    }

    const handleRoleChange = (e) => {
        const newRole = e.target.value;
        setRoleValue(newRole);
        if (newRole === "admin") {
            setSelectedSlugs([]);
        }
    }

    const handleProductToggle = (slug, isSubscribed) => {
        if (!isSubscribed) return;
        setSelectedSlugs(prev =>
            prev.includes(slug)
                ? prev.filter(s => s !== slug)
                : [...prev, slug]
        );
    }

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
        const selectedRole = selectOptions.find(opt => opt.value === roleValue);
        let newPair = {
            email: inputValue,
            role: selectedRole || { value: null, label: "Role" },
            product_slugs: roleValue === "member" ? selectedSlugs : [],
        };
        handleChangeEmailRolePair(index, newPair);
    }, [inputValue, roleValue, selectedSlugs]);

    const isMember = roleValue === "member";

    return (
      <Container>
        <Card isMember={isMember}>
            <TopRow>
                <StyledInput
                    placeholder="Member email"
                    value={inputValue}
                    onChange={handleInputChange}
                    readOnly={readOnlyInput}
                />
                <Divider />
                <SelectWrapper>
                    <StyledSelect
                        value={roleValue}
                        onChange={handleRoleChange}
                    >
                      {showRolePlaceholder && (
                        <option value="" disabled hidden>Role</option>
                      )}
                      {selectOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                              {option.label}
                          </option>
                      ))}
                    </StyledSelect>
                </SelectWrapper>
                {(index != 0 && !noDelete) && (
                    <DeleteButton onClick={() => handleRemove(index)}>
                        <Bin />
                    </DeleteButton>
                )}
            </TopRow>

            {isMember && availableProducts.length > 0 && (
                <>
                    <ProductsDivider />
                    <ProductsRow>
                        {availableProducts.map(product => {
                            const isSelected = selectedSlugs.includes(product.slug);
                            const isSubscribed = product.subscribed;
                            return (
                                <Pill
                                    key={product.slug}
                                    selected={isSelected}
                                    disabled={!isSubscribed}
                                    onClick={() => handleProductToggle(product.slug, isSubscribed)}
                                >
                                    <PillCheck selected={isSelected}>
                                        {isSelected && (
                                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                                <path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </PillCheck>
                                    {product.name}
                                    {!isSubscribed && (
                                        <NoPlanBadge>No plan</NoPlanBadge>
                                    )}
                                </Pill>
                            );
                        })}
                    </ProductsRow>
                    {selectedSlugs.length === 0 && (
                        <ProductsError>
                            Please select at least one product
                        </ProductsError>
                    )}
                </>
            )}
        </Card>
      </Container>
    )
}

export default EmailRolePair

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${props => props.isMember ? lightThemeColors.primaryColor : '#d5d7da'};
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  background: white;
  ${props => props.isMember && `
    box-shadow: 0 0 0 3px rgba(127, 86, 217, 0.1);
  `}
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${lightThemeColors.darkColor};
  background: transparent;
  padding: 12px 14px;
  min-width: 0;

  &::placeholder {
    color: #9ca3af;
  }

  &:read-only {
    background: #f9fafb;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 44px;
  background: #e5e7eb;
  flex-shrink: 0;
`;

const SelectWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const StyledSelect = styled.select`
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
  padding: 12px 40px 12px 14px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${lightThemeColors.primaryColor};
  cursor: pointer;
  min-width: 160px;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%237F56D9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:hover {
    background-color: #f9fafb;
  }

  option {
    color: ${lightThemeColors.darkColor};
  }

  option[value=""] {
    color: #9ca3af;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  margin-right: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #9ca3af;
  opacity: 0;
  pointer-events: none;
  flex-shrink: 0;

  ${Container}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const ProductsDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  width: 100%;
`;

const ProductsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 14px;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid ${props => props.selected ? '#c4b5fd' : '#e5e7eb'};
  background: ${props => props.selected ? '#f4ebff' : 'white'};
  color: ${props => props.selected ? '#6d28d9' : '#374151'};
  font-size: 12px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.4 : 1};
  transition: all 0.15s;
  user-select: none;

  &:hover {
    ${props => !props.disabled && !props.selected && `
      border-color: #c4b5fd;
      background: #faf5ff;
    `}
  }
`;

const PillCheck = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid ${props => props.selected ? '#7f56d9' : '#d1d5db'};
  background: ${props => props.selected ? '#7f56d9' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
`;

const NoPlanBadge = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 1px 6px;
  border-radius: 10px;
`;

const ProductsError = styled.p`
  font-size: 12px;
  color: #ef4444;
  margin: 0;
  padding: 0 14px 10px;
`;